import { FC, ReactNode } from 'react';

/**
 * String form of a placeholder tag as it appears inside the source text
 * (e.g. `'<i>'`, `'</i>'`).
 */
export type EmbeddedStyleStringTag = string;

/**
 * Props accepted by `EmbeddedStyle`.
 */
export type EmbeddedStyleProps = {
  /**
   * Content to be rendered. When it is a `string`, it is scanned for the
   * placeholders declared in `EmbeddedStylePlaceholderName`: the text between
   * each matching open/close tag is wrapped with the component associated to
   * that tag in `EMBEDDED_STYLE_RULES`. Any other `ReactNode` is passed
   * through unchanged.
   */
  children?: ReactNode;
};

/**
 * Tag names used as placeholders inside the source text.
 *
 * Each value is the bare tag name (without `<` / `</` / `>`): the parser builds
 * the actual delimiters at runtime through `openTag(name)` and
 * `closeTag(name)`. Multiple names can point to the same wrapper component
 * (e.g. `BOLD` and `STRONG` are aliases mapped to the same component in
 * `EMBEDDED_STYLE_RULES`).
 *
 * To support a new style add an entry here and a matching rule to
 * `EMBEDDED_STYLE_RULES`.
 */
export enum EmbeddedStylePlaceholderName {
  /** Italic region. Used as `<i>...</i>` in the source text. */
  ITALIC = 'i',
  /** Bold region. Used as `<b>...</b>` in the source text. */
  BOLD = 'b',
  /** Bold region alias. Used as `<strong>...</strong>` in the source text. */
  STRONG = 'strong',
}

/**
 * Associates a placeholder tag name with the component that wraps the text
 * found between its open and close tags.
 */
export type EmbeddedStyleRule = {
  /**
   * Tag name (without angle brackets) of the placeholder this rule matches.
   * Combined with `openTag` / `closeTag` to produce the actual delimiters
   * searched in the input string.
   */
  tagName: EmbeddedStylePlaceholderName;
  /** Inline component used to wrap the content found between `<tagName>` and `</tagName>`. */
  Component: FC<{ children: ReactNode }>;
};
